import { Box, Button, Divider, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BB_REWARD_PAYOUT_CHANNELS,
  BbConfigRow,
  BbRewardPayoutChannel,
  bbGetConfigs,
  bbGetRewardPayoutChannel,
  bbSetConfig,
  bbSetRewardPayoutChannel,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';

// POST /blind-box/configs requires
// bb-tasks:update OR bb-holiday-lottery-config:update OR bb-withdraw-config:update
const CONFIG_UPDATE_PERMS = [
  { resource: 'bb-tasks', action: 'update' },
  { resource: 'bb-holiday-lottery-config', action: 'update' },
  { resource: 'bb-withdraw-config', action: 'update' },
];

type ConfigMeta = {
  label: string;
  unit?: string;
  description?: string;
  i18nDescription?: string;
  /** 若为 true，则建议只读展示（如地址类） */
  readOnly?: boolean;
};

/** i18n：`module.blindBox.rtCfgLbl_<key>` / `module.blindBox.rtCfgUnit_<suffix>` / `module.blindBox.rtCfgDesc_<key>` */
const RT_CFG_STATIC: Record<
  string,
  { labelKey: string; unitKey?: string; descKey?: string; readOnly?: boolean }
> = {
  withdrawal_min_threshold: {
    labelKey: 'module.blindBox.rtCfgLbl_withdrawal_min_threshold',
    unitKey: 'module.blindBox.rtCfgUnit_usdc',
    descKey: 'module.blindBox.rtCfgDesc_withdrawal_min_threshold',
  },
  daily_withdrawal_limit: {
    labelKey: 'module.blindBox.rtCfgLbl_daily_withdrawal_limit',
    unitKey: 'module.blindBox.rtCfgUnit_times_per_day_utc',
    descKey: 'module.blindBox.rtCfgDesc_daily_withdrawal_limit',
  },
  high_value_review_threshold: {
    labelKey: 'module.blindBox.rtCfgLbl_high_value_review_threshold',
    unitKey: 'module.blindBox.rtCfgUnit_usdc',
    descKey: 'module.blindBox.rtCfgDesc_high_value_review_threshold',
  },

  xp_rule_daily_login: {
    labelKey: 'module.blindBox.rtCfgLbl_xp_rule_daily_login',
    unitKey: 'module.blindBox.rtCfgUnit_xp',
    descKey: 'module.blindBox.rtCfgDesc_xp_rule_daily_login',
  },
  xp_rule_trade: {
    labelKey: 'module.blindBox.rtCfgLbl_xp_rule_trade',
    unitKey: 'module.blindBox.rtCfgUnit_xp',
    descKey: 'module.blindBox.rtCfgDesc_xp_rule_trade',
  },
  xp_rule_box_open: {
    labelKey: 'module.blindBox.rtCfgLbl_xp_rule_box_open',
    unitKey: 'module.blindBox.rtCfgUnit_xp',
    descKey: 'module.blindBox.rtCfgDesc_xp_rule_box_open',
  },
  xp_rule_invite: {
    labelKey: 'module.blindBox.rtCfgLbl_xp_rule_invite',
    unitKey: 'module.blindBox.rtCfgUnit_xp_per_person',
    descKey: 'module.blindBox.rtCfgDesc_xp_rule_invite',
  },
  xp_rule_social_follow: {
    labelKey: 'module.blindBox.rtCfgLbl_xp_rule_social_follow',
    unitKey: 'module.blindBox.rtCfgUnit_xp_per_item',
    descKey: 'module.blindBox.rtCfgDesc_xp_rule_social_follow',
  },
  xp_rule_leaderboard_top: {
    labelKey: 'module.blindBox.rtCfgLbl_xp_rule_leaderboard_top',
    unitKey: 'module.blindBox.rtCfgUnit_xp',
    descKey: 'module.blindBox.rtCfgDesc_xp_rule_leaderboard_top',
  },

  draw_address: {
    labelKey: 'module.blindBox.rtCfgLbl_draw_address',
    unitKey: 'module.blindBox.rtCfgUnit_address',
    descKey: 'module.blindBox.rtCfgDesc_draw_address',
    // readOnly: true,
  },

  lottery_tickets_per_box_open: {
    labelKey: 'module.blindBox.rtCfgLbl_lottery_tickets_per_box_open',
    unitKey: 'module.blindBox.rtCfgUnit_tickets',
    descKey: 'module.blindBox.rtCfgDesc_lottery_tickets_per_box_open',
  },
  lottery_ticket_vol_threshold_1: {
    labelKey: 'module.blindBox.rtCfgLbl_lottery_ticket_vol_threshold_1',
    unitKey: 'module.blindBox.rtCfgUnit_usdt',
    descKey: 'module.blindBox.rtCfgDesc_lottery_ticket_vol_threshold_1',
  },
  lottery_ticket_vol_count_1: {
    labelKey: 'module.blindBox.rtCfgLbl_lottery_ticket_vol_count_1',
    unitKey: 'module.blindBox.rtCfgUnit_tickets',
    descKey: 'module.blindBox.rtCfgDesc_lottery_ticket_vol_count_1',
  },
  lottery_ticket_vol_threshold_2: {
    labelKey: 'module.blindBox.rtCfgLbl_lottery_ticket_vol_threshold_2',
    unitKey: 'module.blindBox.rtCfgUnit_usdt',
    descKey: 'module.blindBox.rtCfgDesc_lottery_ticket_vol_threshold_2',
  },
  lottery_ticket_vol_count_2: {
    labelKey: 'module.blindBox.rtCfgLbl_lottery_ticket_vol_count_2',
    unitKey: 'module.blindBox.rtCfgUnit_tickets',
    descKey: 'module.blindBox.rtCfgDesc_lottery_ticket_vol_count_2',
  },

  newbie_first_deposit_threshold: {
    labelKey: 'module.blindBox.rtCfgLbl_newbie_first_deposit_threshold',
    unitKey: 'module.blindBox.rtCfgUnit_usdc',
    descKey: 'module.blindBox.rtCfgDesc_newbie_first_deposit_threshold',
  },
};

function getConfigMeta(row: BbConfigRow, t: (key: string) => string): ConfigMeta {
  const def = RT_CFG_STATIC[row.key];
  const label = def ? t(def.labelKey) : row.description || row.key;
  const unit = def?.unitKey ? t(def.unitKey) : undefined;
  const i18nDescription = def?.descKey ? t(def.descKey) : undefined;
  return {
    label,
    unit,
    readOnly: def?.readOnly,
    description: row.description,
    i18nDescription,
  };
}

const PAYOUT_CHANNEL_LABEL_I18N: Record<BbRewardPayoutChannel, string> = {
  HYPERLIQUID_USDC: 'module.blindBox.payoutChannel_HYPERLIQUID_USDC',
  POLYGON_USDC: 'module.blindBox.payoutChannel_POLYGON_USDC',
  POLYGON_PUSD: 'module.blindBox.payoutChannel_POLYGON_PUSD',
};

const PAYOUT_CHANNEL_DESC_I18N: Record<BbRewardPayoutChannel, string> = {
  HYPERLIQUID_USDC: 'module.blindBox.payoutChannelDesc_HYPERLIQUID_USDC',
  POLYGON_USDC: 'module.blindBox.payoutChannelDesc_POLYGON_USDC',
  POLYGON_PUSD: 'module.blindBox.payoutChannelDesc_POLYGON_PUSD',
};

export function SectionRuntimeConfigs({
  refreshKey,
  keyFilter,
  excludeKeys,
  showRewardPayoutChannel,
}: {
  refreshKey: number;
  /** 若提供且存在匹配项则只展示匹配的配置；若无匹配则展示全部 */
  keyFilter?: (key: string) => boolean;
  /** 强制排除指定 key（例如 activity_* 文案已在 ActivityMeta 中维护） */
  excludeKeys?: string[];
  /** 在提现配置分组顶部展示奖励发放渠道选择 */
  showRewardPayoutChannel?: boolean;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const [configs, setConfigs] = useState<BbConfigRow[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [payoutChannel, setPayoutChannel] = useState<BbRewardPayoutChannel>('HYPERLIQUID_USDC');
  const [savedPayoutChannel, setSavedPayoutChannel] = useState<BbRewardPayoutChannel>('HYPERLIQUID_USDC');
  const [payoutChannelLoading, setPayoutChannelLoading] = useState(false);

  const load = useCallback(() => {
    bbGetConfigs()
      .then(setConfigs)
      .catch(() => setConfigs([]));
    if (showRewardPayoutChannel) {
      setPayoutChannelLoading(true);
      bbGetRewardPayoutChannel()
        .then((value) => {
          setPayoutChannel(value);
          setSavedPayoutChannel(value);
        })
        .catch((e: Error) => {
          notify(e?.message || 'Error', { type: 'error' });
        })
        .finally(() => setPayoutChannelLoading(false));
    }
  }, [notify, showRewardPayoutChannel]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    // 初始化/刷新本地输入值（保留用户正在编辑的值）
    setValues((prev) => {
      const next = { ...prev };
      configs.forEach((c) => {
        if (next[c.key] === undefined) next[c.key] = c.value ?? '';
      });
      return next;
    });
  }, [configs]);

  const saveCfg = async (row: BbConfigRow) => {
    const v = values[row.key] ?? row.value ?? '';
    try {
      await bbSetConfig(row.key, v);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const savePayoutChannel = async () => {
    if (payoutChannel === savedPayoutChannel) return;
    try {
      await bbSetRewardPayoutChannel(payoutChannel);
      setSavedPayoutChannel(payoutChannel);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const renderPayoutChannelRow = () => (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', md: 'center' }}
    >
      <Box sx={{ minWidth: 260 }}>
        <Typography variant="body2">{t('module.blindBox.rewardPayoutChannel')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('module.blindBox.rewardPayoutChannelDesc')}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Select
          size="small"
          value={payoutChannel}
          onChange={(e) => setPayoutChannel(e.target.value as BbRewardPayoutChannel)}
          disabled={payoutChannelLoading}
          fullWidth
          renderValue={(value) => t(PAYOUT_CHANNEL_LABEL_I18N[value as BbRewardPayoutChannel])}
        >
          {BB_REWARD_PAYOUT_CHANNELS.map((value) => (
            <MenuItem key={value} value={value}>
              <Box>
                <Typography variant="body2">{t(PAYOUT_CHANNEL_LABEL_I18N[value])}</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {t(PAYOUT_CHANNEL_DESC_I18N[value])}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t(PAYOUT_CHANNEL_DESC_I18N[payoutChannel])}
        </Typography>
      </Box>
      <PermissionButton resource="bb-withdraw-config" action="update">
        <Button
          variant="outlined"
          onClick={savePayoutChannel}
          disabled={payoutChannelLoading || payoutChannel === savedPayoutChannel}
        >
          {t('module.blindBox.save')}
        </Button>
      </PermissionButton>
    </Stack>
  );

  const excluded = useMemo(() => new Set(excludeKeys || []), [excludeKeys]);

  const filtered = useMemo(() => {
    let list = configs.filter((c) => !excluded.has(c.key));
    if (keyFilter && list.some((c) => keyFilter(c.key))) list = list.filter((c) => keyFilter(c.key));
    return list;
  }, [configs, excluded, keyFilter]);

  const groups = useMemo(() => {
    const byKey = new Map(filtered.map((c) => [c.key, c]));
    const take = (keys: string[]) => keys.map((k) => byKey.get(k)).filter(Boolean) as BbConfigRow[];
    const used = new Set<string>();
    const addUsed = (rows: BbConfigRow[]) => rows.forEach((r) => used.add(r.key));

    const g1 = take(['withdrawal_min_threshold', 'daily_withdrawal_limit', 'high_value_review_threshold']);
    addUsed(g1);
    // const g2 = take([
    //   'xp_rule_daily_login',
    //   'xp_rule_trade',
    //   'xp_rule_box_open',
    //   'xp_rule_invite',
    //   'xp_rule_social_follow',
    //   'xp_rule_leaderboard_top',
    // ]);
    // addUsed(g2);
    const g3 = take([
      'lottery_tickets_per_box_open',
      'lottery_ticket_vol_threshold_1',
      'lottery_ticket_vol_count_1',
      'lottery_ticket_vol_threshold_2',
      'lottery_ticket_vol_count_2',
      'draw_address',
    ]);
    addUsed(g3);
    const g4 = take(['newbie_first_deposit_threshold']);
    addUsed(g4);

    // const rest = filtered
    //   .filter((c) => !used.has(c.key))
    //   .slice()
    //   .sort((a, b) => a.key.localeCompare(b.key));

    return [
      { id: 'withdraw', title: t('module.blindBox.withdrawConfig'), rows: g1 },
      // { title: t('module.blindBox.xpRules'), rows: g2 },
      { id: 'lottery', title: t('module.blindBox.holidayLottery'), rows: g3 },
      { id: 'tasks', title: t('module.blindBox.tasks'), rows: g4 },
      // { title: t('module.blindBox.otherConfigs'), rows: rest },
    ].filter((g) => g.rows.length || (showRewardPayoutChannel && g.id === 'withdraw'));
  }, [filtered, t, showRewardPayoutChannel]);

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">{t('module.blindBox.runtimeConfigs')}</Typography>
      {groups.map((g, idx) => (
        <Box key={g.id}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {g.title}
          </Typography>
          <Stack spacing={1.5}>
            {showRewardPayoutChannel && g.id === 'withdraw' && renderPayoutChannelRow()}
            {g.rows.map((c) => {
              const meta = getConfigMeta(c, t);
              const unitText = meta.unit ? `（${meta.unit}）` : '';
              return (
                <Stack
                  key={c.key}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Box sx={{ minWidth: 260 }}>
                    <Typography variant="body2">{`${meta.label}${unitText}`}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {/*{c.key}*/}
                      {/*{meta.description ? ` — ${meta.description}` : ''}*/}
                      {/*{meta.i18nDescription ? ` — ${meta.i18nDescription}` : meta.description ? ` — ${meta.description}` : ''}*/}
                      {`${meta.i18nDescription}`}
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    value={values[c.key] ?? c.value ?? ''}
                    onChange={(e) =>
                      setValues((p) => ({
                        ...p,
                        [c.key]: e.target.value,
                      }))
                    }
                    sx={{ flex: 1 }}
                    disabled={!!meta.readOnly}
                    placeholder={meta.unit ? meta.unit : undefined}
                  />
                  <PermissionGuard
                    anyOf={CONFIG_UPDATE_PERMS}
                    fallback={null}
                    loadingFallback={null}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => saveCfg(c)}
                      disabled={
                        !!meta.readOnly || (values[c.key] ?? c.value ?? '') === (c.value ?? '')
                      }
                    >
                      {t('module.blindBox.save')}
                    </Button>
                  </PermissionGuard>
                </Stack>
              );
            })}
          </Stack>
          {idx !== groups.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
    </Stack>
  );
}
